// src/features/pdf/pdf-compressor/PdfCompressor.tsx
import React, { useState, useCallback } from 'react';
import {
    Box, Container, Typography, Stack,
    RadioGroup, FormControlLabel, Radio, Collapse, Slider, Checkbox, TextField, Card, CardContent, FormControl, FormLabel
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { Accept } from 'react-dropzone';

import PDFToolWorkspace from '../components/PDFToolWorkspace';
import ToolActionArea, { ToolStatus, OutputFile } from '../components/ToolActionArea';
import PdfViewerModal, { ViewerFile } from '../components/PdfViewerModal';
import { compressPdf, downloadFile } from '../utils/pdfUtils';
import StyledFAQ from '../components/StyledFAQ';
import CompressionResultDisplay from './CompressionResultDisplay';

// Define a simple type for preset values
type CompressionPreset = 'recommended' | 'extreme' | 'custom';

interface FileWithId {
    id: string;
    file: File;
}

const PdfCompressor: React.FC = () => {
    const { t } = useTranslation();
    const [files, setFiles] = useState<FileWithId[]>([]);
    const [status, setStatus] = useState<ToolStatus>('idle');
    const [outputFiles, setOutputFiles] = useState<OutputFile[]>([]);
    const [error, setError] = useState<string>('');

    // Options State
    const [preset, setPreset] = useState<CompressionPreset>('recommended');
    const [customQuality, setCustomQuality] = useState(75); // Slider 0-100
    const [isImageCompressionEnabled, setIsImageCompressionEnabled] = useState(true);
    const [imageQuality, setImageQuality] = useState(75); // Slider 0-100

    // Viewer State
    const [viewerFile, setViewerFile] = useState<ViewerFile | null>(null);

    const handleOpenFileViewer = (file: ViewerFile) => setViewerFile(file);
    const handleCloseFileViewer = () => setViewerFile(null);

    const resetProcessingState = useCallback(() => {
        setStatus('idle');
        setOutputFiles([]);
        setError('');
    }, []);

    const handleDrop = useCallback((acceptedFiles: File[]) => {
        const newFile = acceptedFiles[acceptedFiles.length - 1];
        if (newFile) {
            setFiles([{ id: uuidv4(), file: newFile }]);
        }
        resetProcessingState();
    }, [resetProcessingState]);

    const handleDelete = useCallback((fileId: string) => {
        setFiles(currentFiles => currentFiles.filter(f => f.id !== fileId));
        if (files.length === 1) {
            resetProcessingState();
        }
    }, [files, resetProcessingState]);

    const handleCompress = async () => {
        const fileToProcess = files[0]?.file;
        if (!fileToProcess) {
            setError(t('pdf_compress_error_no_file', { ns: 'tools' }));
            setStatus('error');
            return;
        }

        setStatus('loading');
        setError('');

        try {
            // NOTE: Here we map our UI options to the util function options.
            // This abstraction makes it easy to change the UI without touching the core logic.
            const qualityMap = {
                'recommended': 'recommended',
                'extreme' : 'recommended', // for this util, extreme and recommended use same base settings
                'custom': 'custom',
            }

            const result = await compressPdf(fileToProcess, {
                quality: qualityMap[preset] as 'recommended' | 'custom',
                // other custom options can be passed here if pdfUtils supports them
            });

            // Artificial delay to simulate heavy processing
            await new Promise(resolve => setTimeout(resolve, 1000));

            setOutputFiles([result]);
            setStatus('success');
        } catch (e: any) {
            console.error(e);
            setError(t('pdf_compress_error_generic', { ns: 'tools' }));
            setStatus('error');
        }
    };

    const handleReset = useCallback(() => {
        setFiles([]);
        resetProcessingState();
        setPreset('recommended');
    }, [resetProcessingState]);

    const handleDownload = () => {
        if (outputFiles.length > 0) {
            const file = outputFiles[0];
            downloadFile(file.data, file.name);
        }
    };

    const handlePreviewOutput = (fileName: string) => {
        const fileToPreview = outputFiles.find(f => f.name === fileName);
        if (fileToPreview) {
            handleOpenFileViewer(fileToPreview);
        }
    };

    const acceptedFiles: Accept = { 'application/pdf': ['.pdf'] };

    const faqs = [
        { q: 'pdf_compress_faq1_q', a: 'pdf_compress_faq1_a' },
        { q: 'pdf_compress_faq2_q', a: 'pdf_compress_faq2_a' },
        { q: 'pdf_compress_faq3_q', a: 'pdf_compress_faq3_a' },
    ];

    const originalFileSize = files.length > 0 ? files[0].file.size : 0;
    const compressedFileSize = outputFiles.length > 0 ? outputFiles[0].size : 0;

    return (
        <>
            <PdfViewerModal open={viewerFile !== null} onClose={handleCloseFileViewer} file={viewerFile} />
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Stack spacing={4} alignItems="center">
                    <PDFToolWorkspace
                        titleKey="pdf_compress"
                        descKey="pdf_compress_desc_long"
                        acceptedFiles={acceptedFiles}
                        allowMultiple={false}
                        allowReordering={false}
                        disabled={status === 'loading'}
                        files={files}
                        onDrop={handleDrop}
                        onDelete={handleDelete}
                        onReorder={()=>{}} // Not used
                        onFileClick={handleOpenFileViewer}
                    >
                        {/* Compression Options */}
                        <Card variant="outlined" sx={{ width: '100%', mt: 2 }}>
                            <CardContent>
                                <FormControl component="fieldset">
                                    <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>{t('pdf_compress_options_title', { ns: 'tools' })}</FormLabel>
                                    <RadioGroup row value={preset} onChange={(e) => setPreset(e.target.value as CompressionPreset)}>
                                        <FormControlLabel value="recommended" control={<Radio />} label={t('pdf_compress_preset_recommended', { ns: 'tools' })} />
                                        <FormControlLabel value="extreme" control={<Radio />} label={t('pdf_compress_preset_extreme', { ns: 'tools' })} />
                                        <FormControlLabel value="custom" control={<Radio />} label={t('pdf_compress_preset_custom', { ns: 'tools' })} />
                                    </RadioGroup>
                                </FormControl>
                                <Collapse in={preset === 'custom'}>
                                    <Box sx={{ mt: 2, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                                        <Typography gutterBottom>{t('pdf_compress_custom_quality_label', { ns: 'tools' })}</Typography>
                                        <Slider value={customQuality} onChange={(e, v) => setCustomQuality(v as number)} aria-labelledby="custom-quality-slider" valueLabelDisplay="auto" />

                                        <FormControlLabel control={<Checkbox checked={isImageCompressionEnabled} onChange={(e) => setIsImageCompressionEnabled(e.target.checked)} />} label={t('pdf_compress_custom_image_compress_label', { ns: 'tools' })} />
                                        <Collapse in={isImageCompressionEnabled}>
                                            <Box sx={{ ml: 4 }}>
                                                <Typography gutterBottom>{t('pdf_compress_custom_image_quality_label', { ns: 'tools' })}</Typography>
                                                <Slider value={imageQuality} onChange={(e, v) => setImageQuality(v as number)} aria-labelledby="image-quality-slider" valueLabelDisplay="auto" />
                                            </Box>
                                        </Collapse>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label={t('pdf_compress_custom_max_size_label', { ns: 'tools' })}
                                            placeholder="e.g., 5"
                                            variant="outlined"
                                            margin="normal"
                                            size="small"
                                            InputProps={{
                                                endAdornment: <Typography>MB</Typography>
                                            }}
                                        />
                                    </Box>
                                </Collapse>
                            </CardContent>
                        </Card>
                    </PDFToolWorkspace>

                    <ToolActionArea
                        status={status}
                        onAction={handleCompress}
                        onReset={handleReset}
                        onDownloadAll={outputFiles.length > 0 ? handleDownload : undefined}
                        onPreviewOutput={handlePreviewOutput}
                        actionButtonText={t('pdf_compress_action_button', { ns: 'tools' })}
                        successTitle={t('pdf_compress_success_title', { ns: 'tools' })}
                        successMessage={t('pdf_compress_success_desc', { ns: 'tools' })}
                        errorMessage={error}
                        outputFiles={outputFiles}
                        disabled={files.length === 0 || status === 'loading'}
                    >
                        {/* This is the custom result display passed as a child */}
                        <CompressionResultDisplay
                            originalSize={originalFileSize}
                            newSize={compressedFileSize}
                        />
                    </ToolActionArea>

                </Stack>

                <Box sx={{ my: 15, maxWidth: '800px', mx: 'auto' }}>
                    <Typography sx={{ mb: 2 }} variant="h4" component="h2" gutterBottom textAlign="center">
                        {t('how_to_compress_pdf_title', { ns: 'tools' })}
                    </Typography>
                    <Typography sx={{ mb: 6 }} paragraph color="text.secondary" textAlign="center">
                        {t('how_to_compress_pdf_desc', { ns: 'tools' })}
                    </Typography>
                    <StyledFAQ
                        title={t('faq_title', { ns: 'tools' })}
                        faqs={faqs}
                        t={t}
                    />
                </Box>
            </Container>
        </>
    );
};

export default PdfCompressor;